import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HomeBanner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);

  // Fetch active banners from Firestore
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);

        // Query for active banners only
        const bannersQuery = query(
          collection(FIREBASE_DB, 'banners'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        );

        const querySnapshot = await getDocs(bannersQuery);
        const bannersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setBanners(bannersList);
      } catch (err) {
        console.error('Error fetching banners:', err);
        setError('Unable to load banners');
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Move to the next banner
  const nextBanner = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Move to the previous banner
  const prevBanner = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  };

  // Auto-advance carousel
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      nextBanner();
    }, 5000); // Change banner every 5 seconds
    
    return () => clearInterval(interval);
  }, [banners.length, currentIndex]);

  // If loading, show a placeholder
  if (loading) {
    return (
      <div className="w-full animate-pulse bg-gray-200 rounded-md" style={{ height: '200px' }}></div>
    );
  }

  // If no banners, don't show anything
  if (!banners.length) {
    return <div className="w-full h-0" />;
  }

  const currentBanner = banners[currentIndex];

  return (
    <div className="relative w-full rounded-lg overflow-hidden">
      {banners.length > 1 && (
        <>
          <button 
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full z-10 hover:bg-opacity-75"
            onClick={prevBanner}
            aria-label="Previous banner"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full z-10 hover:bg-opacity-75" 
            onClick={nextBanner}
            aria-label="Next banner"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      <div 
        style={{ 
          height: currentBanner.height || '200px',
          backgroundColor: currentBanner.backgroundColor || '#f0f0f0'
        }} 
        className="w-full flex items-center justify-center relative"
      >
        {/* Background image if available and not text-only */}
        {!currentBanner.isTextOnly && currentBanner.imageUrl && (
          <img 
            src={currentBanner.imageUrl} 
            alt={currentBanner.title || ''} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Banner content */}
        <div className="relative z-1 text-center p-6 max-w-3xl">
          {/* For scrolling text banners */}
          {currentBanner.isTextOnly && currentBanner.scrollText ? (
            <div className="overflow-hidden whitespace-nowrap">
              <div className="scrolling-text">
                <h2 className="text-2xl font-bold mb-2">{currentBanner.title}</h2>
                {currentBanner.subtitle && (
                  <p className="text-lg">{currentBanner.subtitle}</p>
                )}
              </div>
            </div>
          ) : (
            /* Standard banner content */
            <>
              {currentBanner.title && (
                <h2 className="text-2xl font-bold mb-2 text-white drop-shadow-md">{currentBanner.title}</h2>
              )}
              {currentBanner.subtitle && (
                <p className="text-lg mb-4 text-white drop-shadow-md">{currentBanner.subtitle}</p>
              )}
              {currentBanner.buttonText && currentBanner.buttonLink && (
                <a 
                  href={currentBanner.buttonLink}
                  className="inline-block px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                >
                  {currentBanner.buttonText}
                </a>
              )}
            </>
          )}
        </div>
      </div>

      {/* Banner indicators for multiple banners */}
      {banners.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center">
          {banners.map((banner, index) => (
            <button
              key={banner.id}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 w-2 rounded-full mx-1 ${
                index === currentIndex 
                  ? 'bg-primary' 
                  : 'bg-gray-300'
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* CSS for scrolling text */}
      <style jsx>{`
        .scrolling-text {
          display: inline-block;
          padding-left: 100%;
          animation: scroll-left 15s linear infinite;
          white-space: nowrap;
        }
        
        .scrolling-text h2,
        .scrolling-text p {
          display: inline;
          margin-right: 2rem;
        }

        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

export default HomeBanner; 